import { Fragment, useEffect, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useCreateHelpdeskFAQMutation,
  useGetHelpdeskFAQByIdQuery,
  useUpdateHelpdeskFAQMutation,
} from '@/services/HelpdeskFAQService';
import { languageOptions } from '@/types/cms.types';
import FAQTranslationWorkflowSection from './FAQTranslationWorkflowSection';

const HelpdeskFAQForm = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    source_language: 'en',
  });
  const [errors, setErrors] = useState<{
    title?: string;
    body?: string;
  }>({});

  const {
    data: faqData,
    isLoading: isFetchingFAQ,
    refetch,
  } = useGetHelpdeskFAQByIdQuery(id || '', {
    skip: !id,
  });

  const [createFAQ, { isLoading: isCreating }] = useCreateHelpdeskFAQMutation();
  const [updateFAQ, { isLoading: isUpdating }] = useUpdateHelpdeskFAQMutation();

  useEffect(() => {
    if (!faqData?.data) return;

    setFormData({
      title: faqData.data.title,
      body: faqData.data.body,
      source_language: faqData.data.language,
    });
  }, [faqData]);

  const validateForm = (): boolean => {
    const nextErrors: { title?: string; body?: string } = {};

    if (!formData.title.trim()) {
      nextErrors.title = 'Title is required';
    }

    if (!formData.body.trim()) {
      nextErrors.body = 'Body content is required';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      enqueueSnackbar('Please fill in all required fields', {
        variant: 'error',
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditMode && id) {
        await updateFAQ({
          id,
          title: formData.title,
          body: formData.body,
          source_language: formData.source_language,
        }).unwrap();

        enqueueSnackbar('Source FAQ content saved successfully', {
          variant: 'success',
        });
        await refetch();
        return;
      }

      const response = await createFAQ({
        title: formData.title,
        body: formData.body,
        source_language: formData.source_language,
      }).unwrap();

      enqueueSnackbar('FAQ created successfully. You can now manage translations.', {
        variant: 'success',
      });
      navigate(`/helpdesk-faq/edit/${response.data.id}`);
    } catch {
      enqueueSnackbar(
        `Failed to ${isEditMode ? 'save source FAQ content' : 'create FAQ'}`,
        {
          variant: 'error',
        }
      );
    }
  };

  const handleCancel = () => {
    navigate('/helpdesk-faq');
  };

  if (isEditMode && isFetchingFAQ) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <div className="text-center py-4">Loading FAQ...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle
                text={isEditMode ? 'Edit FAQ' : 'Create New FAQ'}
              />
              <ToolbarDescription>
                {isEditMode
                  ? 'Update source content, then manage translation drafts and publishing below.'
                  : 'Create the source FAQ first, then generate and review translation drafts.'}
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-10"
                onClick={handleCancel}
              >
                <KeenIcon icon="arrow-left" />
                Back to List
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed space-y-6">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              {isEditMode ? 'Source FAQ Content' : 'New FAQ'}
            </h2>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              {!isEditMode && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                  Save the FAQ once to unlock the draft, publish, and rollback
                  translation workflow.
                </div>
              )}

              <div>
                <label
                  className="form-label required"
                  htmlFor="source-language-select"
                >
                  Source Language
                </label>
                <select
                  id="source-language-select"
                  value={formData.source_language}
                  onChange={(event) =>
                    setFormData((previous) => ({
                      ...previous,
                      source_language: event.target.value,
                    }))
                  }
                  className="select select-bordered w-full"
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="title" className="form-label required">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(event) => {
                    setFormData((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }));
                    if (errors.title) {
                      setErrors((previous) => ({
                        ...previous,
                        title: undefined,
                      }));
                    }
                  }}
                  className={`input input-bordered w-full ${
                    errors.title ? 'input-error' : ''
                  }`}
                  placeholder="Enter FAQ title"
                  aria-label="FAQ title"
                  autoComplete="off"
                />
                {errors.title && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.title}
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="body" className="form-label required">
                  Body Content
                </label>
                <textarea
                  id="body"
                  value={formData.body}
                  onChange={(event) => {
                    setFormData((previous) => ({
                      ...previous,
                      body: event.target.value,
                    }));
                    if (errors.body) {
                      setErrors((previous) => ({
                        ...previous,
                        body: undefined,
                      }));
                    }
                  }}
                  className={`textarea textarea-bordered w-full h-64 ${
                    errors.body ? 'textarea-error' : ''
                  }`}
                  placeholder="Enter FAQ body content"
                  aria-label="FAQ body content"
                />
                {errors.body && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.body}
                  </span>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  className="btn btn-light"
                  onClick={handleCancel}
                  disabled={isCreating || isUpdating}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isCreating || isUpdating}
                >
                  {isCreating || isUpdating ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {isEditMode ? 'Saving...' : 'Creating...'}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <KeenIcon icon="check" className="mr-2" />
                      {isEditMode ? 'Save Source FAQ' : 'Create FAQ'}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {isEditMode && faqData?.data && (
          <FAQTranslationWorkflowSection faq={faqData.data} onRefresh={refetch} />
        )}
      </div>
    </Fragment>
  );
};

export { HelpdeskFAQForm };
